import { SaleBidStructure } from '__generated__/globalTypes';

import { formatCurrency } from 'configs/columns';

import { SaleOfferings } from 'query/__generated__/sales';

import { formatCalculationDate } from './salesUtils';

export type SaleOffering = SaleOfferings['saleOfferings'][number];

export type BidPrice =
  | { pct_e4: number; amount_cents?: undefined }
  | { pct_e4?: undefined; amount_cents: number };

export type BidTerms = BidPrice & {
  round: number;
  structure: SaleBidStructure;
  offering_ids: string[];
};

export const STRUCTURE_LABEL: Record<SaleBidStructure, string> = {
  [SaleBidStructure.CASH]: 'Cash',
  [SaleBidStructure.JVT]: 'Joint Venture',
};

// A sale is cash-only or a JVT (RFP p.44), but no sale records which yet, and the FDIC's sales so
// far are cash-only. ENG-4187 replaces this with a per-sale setting.
export const SALE_ALLOWS_JOINT_VENTURE_BIDS = false;

export const formatPctE4 = (pctE4: number): string => (pctE4 / 10000).toFixed(4);

// Parses a user-entered percent string ("62.3456") into pct_e4 (623456). Unlike a plain
// Number() parse, the regex rejects more than 4 decimal places instead of silently rounding
// them away, and the range check rejects both 0 (not a real bid) and anything that would
// overflow pct_e4's Int32 on the wire (> 214748.3647%).
const PCT_E4_REGEX = /^(?<integer>\d+)(?:\.(?<decimal>\d{1,4}))?$/;
const PCT_E4_MAX = 2147483647;

export const parsePercentToPctE4 = (value: string): number | undefined => {
  const match = value.trim().match(PCT_E4_REGEX);
  if (!match) {
    return undefined;
  }
  const integer = match.groups?.integer ?? '0';
  const decimal = (match.groups?.decimal ?? '').padEnd(4, '0');
  const pctE4 = parseInt(integer, 10) * 10000 + parseInt(decimal, 10);
  return pctE4 > 0 && pctE4 <= PCT_E4_MAX ? pctE4 : undefined;
};

// Also the exact text a bidder types to confirm a final bid, so it must stay one stable token.
export const priceLabel = (price: { pct_e4?: number | null; amount_cents?: any }): string =>
  price.pct_e4 != null ? `${formatPctE4(price.pct_e4)}%` : formatCurrency(price.amount_cents);

// loan-service keys a bid series on round + structure + offering set, in any order.
export const seriesKey = (
  round: number,
  structure: SaleBidStructure,
  offeringIds: readonly string[],
): string => [round, structure, ...[...offeringIds].sort()].join('|');

// BigInt: a large pool's Book Value in cents times pct_e4 runs past Number's exact integers.
const roundedDivide = (numerator: bigint, denominator: bigint): bigint =>
  (numerator * BigInt(2) + denominator) / (denominator * BigInt(2));

const PCT_E4_PER_UNIT = BigInt(1000000);

// Undefined when any offering has no Book Value yet: api leaves current_balance_cents null until
// cashflows has summarised the offering, and a partial sum would understate a linked bid.
export const bookValueCents = (offerings: readonly SaleOffering[]): bigint | undefined =>
  offerings.some((offering) => offering.current_balance_cents == null)
    ? undefined
    : offerings.reduce(
        (sum, offering) => sum + BigInt(Math.round(Number(offering.current_balance_cents))),
        BigInt(0),
      );

export const dollarEquivalentCents = (bookValue: bigint, pctE4: number): number =>
  Number(roundedDivide(bookValue * BigInt(pctE4), PCT_E4_PER_UNIT));

export const impliedPctE4 = (amountCents: number, bookValue: bigint): number | undefined =>
  bookValue > BigInt(0)
    ? Number(roundedDivide(BigInt(amountCents) * PCT_E4_PER_UNIT, bookValue))
    : undefined;

export const BOOK_VALUE_UNAVAILABLE_COPY = 'Book Value not available';

export const bookValueAsOfLabel = (calculationDate: string): string =>
  `of Book Value as of ${formatCalculationDate(calculationDate)}`;

// Undefined only for a $ bid on pools whose Book Value is zero, which implies no %.
export const priceEquivalentLabel = (
  price: BidPrice,
  bookValue: bigint | undefined,
  calculationDate: string,
): string | undefined => {
  if (bookValue == null) {
    return BOOK_VALUE_UNAVAILABLE_COPY;
  }
  if (price.pct_e4 != null) {
    return `= ${formatCurrency(dollarEquivalentCents(bookValue, price.pct_e4))}`;
  }
  const implied = impliedPctE4(price.amount_cents, bookValue);
  return implied == null
    ? undefined
    : `= ${formatPctE4(implied)}% ${bookValueAsOfLabel(calculationDate)}`;
};
